import {
  UNSAFE_DataRouterContext,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router";
import { useCallback, useContext, useRef } from "react";
import { isValidValue } from "../utils";

/**
 * Custom hook to manage query params (single or multiple keys)
 *
 * Supports:
 * - Single key mode → [value, setValue]
 * - Bulk update mode → setValue({ page: 2, status: 'past' })
 * - Always works on the router's latest URL, even when another component
 *   navigated earlier in the same render (a stale URL would undo it)
 * - setValue(value, { replace: true }) updates the URL without adding a
 *   browser-history entry (for automatic updates such as resetting the page
 *   on mount or infinite scroll, so Back goes to the previous page)
 */
const useQueryParam = (key, defaultValue = null) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // The app's data router knows the live location; other routers (tests'
  // MemoryRouter) fall back to the location of the last render
  const router = useContext(UNSAFE_DataRouterContext)?.router;
  const location = useLocation();
  const locationRef = useRef(location);
  locationRef.current = location;

  const value = searchParams.get(key) ?? defaultValue;

  const setValue = useCallback(
    (newValue, options) => {
      const replace = !!options?.replace;
      const { pathname, search } = router?.state.location ?? locationRef.current;
      const params = new URLSearchParams(search);

      if (isValidValue(newValue) && typeof newValue === "object") {
        // Bulk update
        Object.entries(newValue).forEach(([k, v]) => {
          if (!isValidValue(v)) {
            params.delete(k);
          } else {
            params.set(k, v);
          }
        });
      } else if (key) {
        // Single key mode
        if (!isValidValue(newValue) || newValue === defaultValue) {
          params.delete(key);
        } else {
          params.set(key, newValue);
        }
      } else if (newValue === null || newValue === undefined) {
        // Clear all
        params.forEach((_, k) => params.delete(k));
      }

      // Nothing changed (e.g. the list's "back to page 1" on mount when the
      // URL already says page 1): don't navigate, so an automatic update
      // can never step on a navigation the user just started
      if (params.toString() === new URLSearchParams(search).toString()) return;

      navigate({ pathname, search: params.toString() }, { replace });
    },
    [key, defaultValue, navigate, router]
  );

  return key ? [value, setValue] : setValue;
};

export default useQueryParam;
